/**
 * Logger utility that wraps OpenTelemetry logging
 * Provides a simple interface for logging throughout the application
 */

import { logs, SeverityNumber } from '@opentelemetry/api'
import { isLoggerInitialized, getServiceName } from './otlpLogger'

// Initialize logger if not already done
let otelLoggerInstance: ReturnType<typeof logs.getLogger> | null = null

function getLogger() {
  if (!otelLoggerInstance) {
    otelLoggerInstance = logs.getLogger(getServiceName(), process.env.APP_VERSION || '1.0.0')
  }
  return otelLoggerInstance
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogContext {
  [key: string]: unknown
}

/**
 * Maps log level to OpenTelemetry severity number
 */
function getSeverityNumber(level: LogLevel): SeverityNumber {
  switch (level) {
    case 'debug':
      return SeverityNumber.DEBUG
    case 'info':
      return SeverityNumber.INFO
    case 'warn':
      return SeverityNumber.WARN
    case 'error':
      return SeverityNumber.ERROR
    default:
      return SeverityNumber.INFO
  }
}

/**
 * Safely converts context to attributes for OpenTelemetry
 * Filters out non-serializable values and flattens nested objects
 */
export function contextToAttributes(
  context?: LogContext,
): Record<string, string | number | boolean> {
  if (!context) return {}
  const attributes: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(context)) {
    // Skip null, undefined, functions, and objects
    if (value === null || value === undefined || typeof value === 'function') {
      continue
    }
    // Convert objects and arrays to strings
    if (typeof value === 'object') {
      try {
        attributes[key] = JSON.stringify(value)
      } catch {
        attributes[key] = '[Non-serializable object]'
      }
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      attributes[key] = value
    } else {
      // Convert other types to string
      attributes[key] = String(value)
    }
  }
  return attributes
}

/**
 * Logs a message with the specified level and optional context
 * Falls back to console logging if OpenTelemetry is not initialized
 */
function log(level: LogLevel, message: string, context?: LogContext): void {
  // Check if logger provider is initialized before attempting to use OTLP
  // On the client side, the provider is never initialized, so we need to check this
  // to avoid silently using the no-op logger
  if (!isLoggerInitialized()) {
    // Fallback to console logging if OpenTelemetry is not initialized
    let contextStr = ''
    if (context) {
      try {
        contextStr = ` ${JSON.stringify(context)}`
      } catch (_error) {
        contextStr = ' [context could not be stringified]'
      }
    }
    const consoleMethod =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
    consoleMethod(`[${level.toUpperCase()}] ${message}${contextStr}`)
    return
  }

  try {
    const otelLogger = getLogger()
    const severityNumber = getSeverityNumber(level)

    otelLogger.emit({
      severityNumber,
      severityText: level.toUpperCase(),
      body: message,
      attributes: {
        ...contextToAttributes(context),
      },
    })
  } catch (_error) {
    // Fallback to console logging if OpenTelemetry fails
    let contextStr = ''
    if (context) {
      try {
        contextStr = ` ${JSON.stringify(context)}`
      } catch (_error) {
        contextStr = ' [context could not be stringified]'
      }
    }
    const consoleMethod =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
    consoleMethod(`[${level.toUpperCase()}] [${new Date().toISOString()}] ${message}${contextStr}`)
  }
}

/**
 * Logger interface for easy use throughout the application
 */
export const logger = {
  /**
   * Log a debug message
   */
  debug: (message: string, context?: LogContext) => {
    log('debug', message, context)
  },

  /**
   * Log an info message
   */
  info: (message: string, context?: LogContext) => {
    log('info', message, context)
  },

  /**
   * Log a warning message
   */
  warn: (message: string, context?: LogContext) => {
    log('warn', message, context)
  },

  /**
   * Log an error message
   */
  error: (message: string, context?: LogContext) => {
    log('error', message, context)
  },
}
